import type { PaymentMethod, PaymentRecord } from "../types/payments.types";

/** Nested relation objects returned by backend after JOINs */
type BackendClient = {
	id: string;
	firstName: string;
	lastName: string;
	surname?: string | null;
};

type BackendBuilding = {
	id: string;
	name: string;
};

type BackendBlock = {
	id: string;
	name?: string;
	building: BackendBuilding;
};

type BackendSection = {
	id: string;
	block: BackendBlock;
};

type BackendRoom = {
	id: string;
	roomNumber: string;
	section: BackendSection;
};

type BackendSaleNested = {
	id: string;
	saleNumber?: number | string | null;
	client: BackendClient;
	room: BackendRoom;
};

type BackendUser = {
	id: string;
	firstName: string;
	lastName: string;
};

export type BackendPayment = {
	id: string;
	orgId: string;
	saleId: string;
	/** Now a nested user object (was raw UUID before backend fix) */
	receivedBy: BackendUser | string;
	amount: number;
	discountAmount: number;
	monthsPaid: number;
	currency: string;
	paymentMethod: PaymentMethod;
	paymentDate: string;
	receiptNumber: string | null;
	note: string | null;
	createdAt: string;
	updatedAt?: string;
	isDeleted?: boolean;
	deletedBy?: string | null;
	deletedAt?: string | null;
	refundedAmount?: number;
	refundedAt?: string | null;
	refundReason?: string | null;
	/** Nested sale with client + room → section → block → building */
	sale?: BackendSaleNested;
};

export type CreatePaymentFormValues = {
	amountUzs: number;
	discountAmountUzs?: number;
	monthsPaid?: number;
	note?: string;
	paymentDate: string;
	paymentMethod: PaymentMethod;
	receiptNumber?: string;
	saleId: string;
	scheduleIds: string[];
};

export type CreatePaymentRequest = {
	amount: number;
	discountAmount: number;
	monthsPaid: number;
	note?: string;
	paymentDate: string;
	paymentMethod: PaymentMethod;
	receiptNumber?: string;
	saleId: string;
	scheduleIds: string[];
};

export function toCreatePaymentRequest(values: CreatePaymentFormValues): CreatePaymentRequest {
	const receiptNumber = values.receiptNumber?.trim();
	const note = values.note?.trim();

	return {
		amount: Math.round(values.amountUzs),
		discountAmount: Math.round(values.discountAmountUzs ?? 0),
		monthsPaid: values.monthsPaid ?? 1,
		...(note ? { note } : {}),
		paymentDate: values.paymentDate,
		paymentMethod: values.paymentMethod,
		...(receiptNumber ? { receiptNumber } : {}),
		saleId: values.saleId,
		scheduleIds: values.scheduleIds,
	};
}

function resolveReceivedByName(receivedBy: BackendUser | string): string {
	if (typeof receivedBy === "object" && receivedBy !== null) {
		return `${receivedBy.firstName} ${receivedBy.lastName}`.trim() || "-";
	}
	return String(receivedBy);
}

export function mapBackendPayment(payment: BackendPayment): PaymentRecord {
	const sale = payment.sale;
	const client = sale?.client;
	const room = sale?.room;
	const building = room?.section?.block?.building;

	return {
		id: payment.id,
		saleId: payment.saleId,
		saleNumber:
			sale && sale.saleNumber !== undefined && sale.saleNumber !== null
				? String(sale.saleNumber)
				: sale
					? sale.id.substring(0, 6).toUpperCase()
					: payment.saleId.substring(0, 6).toUpperCase(),
		scheduleIndex: null,
		clientName: client
			? [client.lastName, client.firstName, client.surname].filter(Boolean).join(" ")
			: "-",
		roomNumber: room?.roomNumber ?? "-",
		buildingName: building?.name ?? "-",
		amountUzs: payment.amount,
		discountAmountUzs: payment.discountAmount,
		monthsPaid: payment.monthsPaid,
		paymentMethod: payment.paymentMethod,
		paymentDate: payment.paymentDate,
		receiptNumber: payment.receiptNumber ?? "-",
		receivedBy: resolveReceivedByName(payment.receivedBy),
		note: payment.note ?? undefined,
		createdAt: payment.createdAt,
		refundedAmount: payment.refundedAmount ?? 0,
		refundedAt: payment.refundedAt,
		refundReason: payment.refundReason,
	};
}
